import {
  Button,
  Checkbox,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@1trading/react';
import { type FormEvent, useState } from 'react';
import type { FilterType, MatchMode } from './types';

export interface FilterEditDialogProps {
  title: string;
  type: FilterType;
  /** `multi-text` only — picks the default helper copy (overridden by `helperText`); the
   * actual matching mode lives on the FilterConfig and is applied by rowMatchesFilters,
   * not by this dialog. */
  matchMode?: MatchMode;
  /**
   * `multi-text` only — overrides the matchMode-derived helper paragraph. Use this when the
   * column is server-filtered and the actual matching algorithm bop-api applies isn't
   * verified from this codebase — asserting a specific mode
   * ("case-insensitive substring") in that case would be an unverified, potentially
   * misleading claim. Pass '' to omit the helper line entirely.
   */
  helperText?: string;
  /** `multi-choice` / `select` only */
  options: string[];
  /** Current committed value(s) for this column — see FilterState in types.ts for the shape
   * per FilterType (0-1 entries for multi-text/select, 0-N for multi-choice, [from, to] for
   * range/date). */
  initial: string[];
  onApply: (values: string[]) => void;
  onClose: () => void;
}

export const FilterEditDialog = ({ title, type, matchMode = 'exact', helperText, options, initial, onApply, onClose }: FilterEditDialogProps) => {
  const [text, setText] = useState(initial[0] ?? '');
  const [selection, setSelection] = useState<string[]>(initial);
  const [selectValue, setSelectValue] = useState(initial[0] ?? '');
  const [rangeFrom, setRangeFrom] = useState(initial[0] ?? '');
  const [rangeTo, setRangeTo] = useState(initial[1] ?? '');
  const [dateFrom, setDateFrom] = useState(initial[0] ?? '');
  const [dateTo, setDateTo] = useState(initial[1] ?? '');

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (type === 'multi-text') {
      const trimmed = text.trim();
      onApply(trimmed ? [trimmed] : []);
      return;
    }
    if (type === 'select') {
      onApply(selectValue ? [selectValue] : []);
      return;
    }
    if (type === 'range') {
      const from = rangeFrom.trim();
      const to = rangeTo.trim();
      onApply(from || to ? [from, to] : []);
      return;
    }
    if (type === 'date') {
      onApply(dateFrom || dateTo ? [dateFrom, dateTo] : []);
      return;
    }
    onApply(selection);
  };

  const toggleOption = (option: string, isChecked: boolean) => setSelection((current) => (isChecked ? [...current, option] : current.filter((entry) => entry !== option)));

  // Resets the DRAFT only — does not commit or close: an apply-immediately Clear was a real
  // interaction regression for every surface except institutions, and contradicts the
  // documented contract for transactions — "Clear" resets the dialog's selection/text;
  // "Apply" is what commits.
  const handleClear = () => {
    setText('');
    setSelection([]);
    setSelectValue('');
    setRangeFrom('');
    setRangeTo('');
    setDateFrom('');
    setDateTo('');
  };

  return (
    <Dialog onOpenChange={(open) => (open ? undefined : onClose())} open>
      <DialogContent className="sm:max-w-sm">
        <DialogHeader>
          <DialogTitle>Filter by {title}</DialogTitle>
        </DialogHeader>
        <form className="flex flex-col gap-4" onSubmit={handleSubmit}>
          {type === 'multi-text' ? (
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="table-filter-value">{title}</Label>
              <Input id="table-filter-value" onChange={(event) => setText(event.target.value)} placeholder={matchMode === 'substring' ? 'Contains…' : 'Exact value'} value={text} />
              {helperText === '' ? null : (
                <p className="text-xs text-muted-foreground">
                  {helperText ?? (matchMode === 'substring' ? 'Matches rows containing this text (case-insensitive).' : 'Matches the exact cell value (legacy behaviour).')}
                </p>
              )}
            </div>
          ) : type === 'select' ? (
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="table-filter-select">{title}</Label>
              <Select onValueChange={setSelectValue} value={selectValue}>
                <SelectTrigger id="table-filter-select">
                  <SelectValue placeholder={`Select ${title}`} />
                </SelectTrigger>
                <SelectContent>
                  {options.map((option) => (
                    <SelectItem key={option} value={option}>
                      {option}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {options.length === 0 ? <p className="text-sm text-muted-foreground">No values available to filter on.</p> : null}
            </div>
          ) : type === 'range' ? (
            <div className="flex gap-3">
              <div className="flex flex-1 flex-col gap-1.5">
                <Label htmlFor="table-filter-range-from">From</Label>
                <Input id="table-filter-range-from" onChange={(event) => setRangeFrom(event.target.value)} type="number" value={rangeFrom} />
              </div>
              <div className="flex flex-1 flex-col gap-1.5">
                <Label htmlFor="table-filter-range-to">To</Label>
                <Input id="table-filter-range-to" onChange={(event) => setRangeTo(event.target.value)} type="number" value={rangeTo} />
              </div>
            </div>
          ) : type === 'date' ? (
            <div className="flex gap-3">
              <div className="flex flex-1 flex-col gap-1.5">
                <Label htmlFor="table-filter-date-from">From</Label>
                <Input id="table-filter-date-from" onChange={(event) => setDateFrom(event.target.value)} type="date" value={dateFrom} />
              </div>
              <div className="flex flex-1 flex-col gap-1.5">
                <Label htmlFor="table-filter-date-to">To</Label>
                <Input id="table-filter-date-to" onChange={(event) => setDateTo(event.target.value)} type="date" value={dateTo} />
              </div>
            </div>
          ) : options.length === 0 ? (
            <p className="text-sm text-muted-foreground">No values available to filter on.</p>
          ) : (
            <div className="flex max-h-64 flex-col gap-2 overflow-y-auto">
              {options.map((option) => (
                <div className="flex items-center gap-2" key={option}>
                  <Checkbox checked={selection.includes(option)} id={`filter-option-${option}`} onCheckedChange={(checked) => toggleOption(option, checked === true)} />
                  <Label className="break-all font-normal" htmlFor={`filter-option-${option}`}>
                    {option}
                  </Label>
                </div>
              ))}
            </div>
          )}
          <DialogFooter>
            <Button onClick={handleClear} type="button" variant="ghost">
              Clear
            </Button>
            <Button onClick={onClose} type="button" variant="outline">
              Cancel
            </Button>
            <Button type="submit" variant="primary">
              Apply
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};
FilterEditDialog.displayName = 'FilterEditDialog';
